<template>
    <v-input
        class="height-imperial-selector-input"
        :error="error"
    >
        <template #default="{ isValid }">
            <v-text-field
                v-model="height"
                class="v-select cursor-pointer"
                :class="{
                    'v-select--active-menu': !!heightMenu,
                    'v-select--selected': !!height
                }"
                variant="outlined"
                :dirty="isDirty"
                :label="label"
                :error="error"
                :clearable="clearable"
                :disabled="disabled"
                :hide-details="true"
                inputmode="none"
                @mousedown="onOpenMenu"
                @keydown="onKeyDown"
                @click:clear="onClear"
            >
                <v-menu
                    v-model="heightMenu"
                    :close-on-content-click="false"
                    :open-on-click="false"
                    :scrollable="false"
                    max-height="310"
                >
                    <template #activator="{ props }">
                        <v-sheet
                            v-bind="props"
                            class="w-100 d-flex overflow-hidden no-user-select"
                            height="56"
                        >
                            <div
                                class="d-flex align-center ps-4 flex-basis-half"
                            >
                                <template
                                    v-if="lo_isNumber(heightFt)"
                                >
                                    {{ heightFt }} ft
                                </template>
                            </div>
                            <v-divider
                                v-if="isDirty"
                                vertical
                            ></v-divider>
                            <div
                                class="d-flex align-center ps-4 flex-basis-half"
                            >
                                <template
                                    v-if="lo_isNumber(heightIn)"
                                >
                                    {{ heightIn }} in
                                </template>
                            </div>
                        </v-sheet>
                    </template>

                    <v-sheet
                        class="d-flex overflow-hidden no-user-select"
                        max-height="310"
                    >
                        <v-list
                            class="flex-basis-half overflow-y-auto pt-0"
                            max-height="310"
                        >
                            <v-list-subheader sticky>Feet</v-list-subheader>
                            <v-virtual-scroll
                                :items="BodyLengthFeetList"
                                max-height="310"
                                renderless
                            >
                                <template #default="{ item, index, itemRef  }">
                                    <v-list-item
                                        :active="heightFt === item.value"
                                        @click="onSelect(item, null)"
                                    >
                                        {{ item.text }}
                                    </v-list-item>
                                </template>
                            </v-virtual-scroll>
                        </v-list>
                        <v-divider
                            style="height: 310px;"
                            vertical
                        ></v-divider>
                        <v-list
                            class="flex-basis-half overflow-y-auto pt-0"
                            max-height="310"
                        >
                            <v-list-subheader sticky>Inches</v-list-subheader>
                            <v-virtual-scroll
                                :items="BodyLengthInchesList"
                                max-height="310"
                                renderless
                            >
                                <template #default="{ item, index, itemRef  }">
                                    <v-list-item
                                        :active="heightFt === item.value"
                                        @click="onSelect(null, item)"
                                    >
                                        {{ item.text }}
                                    </v-list-item>
                                </template>
                            </v-virtual-scroll>
                        </v-list>
                    </v-sheet>
                </v-menu>
            </v-text-field>
        </template>
    </v-input>
</template>

<script lang="ts" setup>
    import { BodyLengthFeetList, BodyLengthInchesList } from '~/models';
    import { isNil as lo_isNil } from 'es-toolkit';
    import { isNumber as lo_isNumber } from 'es-toolkit/compat';
    import { convertFtInToIn, convertInToFtIn } from 'x1-base/utils';
    import type {
        GenericVSelectListItem
    } from 'x1-base/types';

    const props = defineProps<{
        modelValue: Nilish<number>;
        error?: boolean;
        label?: string;
        clearable?: boolean;
        disabled?: boolean;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: Nullable<number>];
    }>();

    const height = ref<Nilish<number>>(props.modelValue);
    const heightFt = ref<Nilish<number>>(null);
    const heightIn = ref<Nilish<number>>(null);
    const heightMenu = ref<boolean>(false);

    const isDirty = computed<boolean>(() => heightMenu.value || !lo_isNil(heightFt.value) || !lo_isNil(heightIn.value));
    let isWatcherPaused = false;

    const unWatchModelValue = watch(
        () => props.modelValue,
        (newVal) => {
            if (!isWatcherPaused) {
                prePopValue(newVal);
            }
        },
        { immediate: true }
    );

    function prePopValue(val: Nilish<number>): void {
        if (!lo_isNumber(val)) {
            return;
        }

        const [f, i] = convertInToFtIn(val);
        const fObj = BodyLengthFeetList.find((o) => o.value === f);
        const iObj = BodyLengthInchesList.find((o) => o.value === i);

        if (fObj) {
            heightFt.value = f;
        }

        if (iObj) {
            heightIn.value = i;
        }
    }

    function onOpenMenu(e: Event): void {
        heightMenu.value = !heightMenu.value;
    }

    function onClear(): void {
        height.value = null;
        heightFt.value = null;
        heightIn.value = null;
    }

    function onSelect(itemFt: Nilish<GenericVSelectListItem>, itemIn: Nilish<GenericVSelectListItem>): void {
        if (itemFt) {
            heightFt.value = itemFt?.value ?? null;
        }

        if (itemIn) {
            heightIn.value = itemIn?.value ?? null;
        }

        onChange();
    }

    function onChange(): void {
        let out: Nullable<number> = null;
        const isNullFt = lo_isNil(heightFt.value);
        const isNullIn = lo_isNil(heightIn.value);

        if (!isNullFt && !isNullIn) {
            out = convertFtInToIn([heightFt.value ?? 0, heightIn.value ?? 0]);

            nextTick(() => {
                heightMenu.value = false;
            });
        }

        height.value = out;
        setCurrent(out);
    }

    function setCurrent(item: Nullable<number>): void {
        isWatcherPaused = true;
        emit('update:modelValue', item);

        setTimeout(() => {
            isWatcherPaused = false;
        }, 100);
    }

    function onKeyDown(e: KeyboardEvent): void {
        if (['Enter', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
            e.preventDefault();
        }

        if (['Enter', 'ArrowDown', ' '].includes(e.key)) {
            heightMenu.value = true;
        }

        if (['Escape', 'Tab'].includes(e.key)) {
            heightMenu.value = false;
        }
    }

    onBeforeUnmount(() => {
        unWatchModelValue();
    });
</script>

<style lang="scss">
.height-imperial-selector-input {
    .v-input__details {
        padding-inline: 16px;
    }

    .v-field__input {
        padding: 0;
    }
}
</style>
